import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSearchParams } from 'react-router-dom'
import { showSuccess } from '@/lib/utils/notifications'
import { ValidationModal } from '@/components/ui/ValidationModal'
import type { ApiError } from '@/lib/api/apiClient'
import { AUTH_CONTENT, getAuthCopyright } from '../constants/authContent'
import {
  CLOSED_MESSAGE_MODAL,
  REQUEST_SENT_TITLE,
  VALIDATION_MESSAGE_TITLE,
} from '../constants/passwordReset'
import { ForgotPasswordView } from '../components/ForgotPasswordView'
import { requestPasswordReset } from '../services/passwordResetService'
import type { MessageModalState } from '../types/passwordReset'
import {
  forgotPasswordFormSchema,
  type ForgotPasswordFormValues,
} from '../types/schemas'

/**
 * Forgot-password page — logic + content wiring only.
 * Presentation lives in ForgotPasswordView.
 */
export function ForgotPassword() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [messageModal, setMessageModal] = useState<MessageModalState>(CLOSED_MESSAGE_MODAL)

  const schema = useMemo(() => forgotPasswordFormSchema, [])

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
    mode: 'onChange',
  })

  useEffect(() => {
    const email = searchParams.get('email')
    if (!email) return
    setValue('email', email, { shouldValidate: true, shouldTouch: true })
    const next = new URLSearchParams(searchParams)
    next.delete('email')
    setSearchParams(next, { replace: true })
    // Prefill once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = handleSubmit(async (data) => {
    setIsSubmitting(true)
    try {
      const message = await requestPasswordReset(data.email.trim().toLowerCase())
      showSuccess(typeof message === 'string' ? message : String(message), 3000)
      reset({ email: '' })
    } catch (error) {
      const apiError = error as ApiError
      const body = apiError.message || AUTH_CONTENT.forgotPassword.sendFailed
      setMessageModal({
        isOpen: true,
        title: apiError.status === 429 ? REQUEST_SENT_TITLE : VALIDATION_MESSAGE_TITLE,
        message: body,
      })
    } finally {
      setIsSubmitting(false)
    }
  })

  return (
    <>
      <ForgotPasswordView
        content={AUTH_CONTENT.forgotPassword}
        copyright={getAuthCopyright()}
        emailError={errors.email?.message}
        isSubmitting={isSubmitting}
        isValid={isValid}
        emailInputProps={register('email')}
        onSubmit={onSubmit}
      />

      <ValidationModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        confirmText={AUTH_CONTENT.common.ok}
        showValidationIcon={false}
        alignMessage
        okButtonStyle="saveChanges"
        onClose={() => setMessageModal(CLOSED_MESSAGE_MODAL)}
      />
    </>
  )
}
